/* eslint-disable max-len */
import _ from 'underscore';
import { jss } from 'react-jss';
import { components, names } from './components';
import type { ILayoutComponent, ILayoutConfig, ILayoutContextStyles } from './types';

type SelectedStyle = Record<string, unknown>;
type SelectedStyles = Partial<Record<ILayoutComponent, SelectedStyle>>;

const calcQueue = [
  names.header,
  /*
  * Currently we only support calculate css "left" from left to right, or calculate css "top" from top to bottom.
  * However the ads copilot will be displayed at the right side of the page, which is not supported by current layout system
  * To resolve this issue, we created a container node for copilot, whose left is 0 and top is the height of the header, and the z-index is 0
  * then we will put the actual copilot component at the right side of this container node.
  */
  names.copilot,
  names.panelContainer,
  names.topBanner,
  names.promotionBanner,
  names.nav,
  names.status,
  names.messageBar,
  names.menu,
  names.tab,
  names.subTab,
  names.content,
];

const getSelection = (config: ILayoutConfig): SelectedStyles => _.mapObject(config, ({
  mode,
  ...advancedOptions
}, key) => {
  const componentName = key as ILayoutComponent;
  const componentStyles = components[componentName] as Record<string, unknown>;
  let result = _.result(componentStyles, mode, {}) as SelectedStyle;

  // i.e. advancedOptions = { showExperimentScoreCard: true }
  _.each(advancedOptions, (advancedOptionValue, advancedOptionName) => {
    const advancedProcessor = componentStyles[advancedOptionName] as ((style: SelectedStyle, value: unknown) => SelectedStyle) | undefined;
    if (advancedOptionValue && _.isFunction(advancedProcessor)) {
      result = advancedProcessor(result, advancedOptionValue);
    }
  });

  return result;
});

export class StylesContainer {
  styles: ILayoutContextStyles;

  constructor(config: ILayoutConfig) {
    const selected = getSelection(config);
    this.styles = _.mapObject(selected, (_item, key) =>
      jss.createStyleSheet(
        { [key]: (style: SelectedStyle) => style } as unknown as Parameters<typeof jss.createStyleSheet>[0],
        { link: true }
      ).attach());
    this.update(config);
  }

  update(config: ILayoutConfig) {
    const selected = getSelection(config);
    const startPos = { top: 0, left: 0 };

    _.each(calcQueue, (key: ILayoutComponent) => {
      const component = selected[key];
      this.styles[key]?.update(_.extend(component, startPos));
      startPos.top += _.result(component, 'height', 0);
      startPos.left += _.result(component, 'width', 0);
    });
  }
}
